import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, Check, RotateCcw } from 'lucide-react';

const SignatureModal = ({ isOpen, onClose, onSave, title, existingSignature }) => {
  const sigRef = useRef();
  const [savedSignature, setSavedSignature] = useState(existingSignature);

  if (!isOpen) return null;

  const handleClear = () => {
    sigRef.current?.clear();
  };

  const handleSave = () => {
    if (!sigRef.current?.isEmpty()) {
      const signatureData = sigRef.current.toDataURL();
      setSavedSignature(signatureData);
      onSave(signatureData);
    } else if (savedSignature) {
      onSave(savedSignature);
    }
  };

  const handleRemove = () => {
    setSavedSignature(null);
    onSave(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[70] flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 flex items-center justify-between">
        <h2 className="text-white text-lg font-bold">{title}</h2>
        <button
          onClick={onClose}
          className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Instructions */}
      <div className="bg-white p-3 border-b border-gray-200">
        <p className="text-sm text-gray-600 text-center">
          ✍️ Sign using your finger or stylus. Hold device horizontally for best experience.
        </p>
      </div>

      {/* Signature Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 bg-gray-100">
        {savedSignature ? (
          <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl p-4">
            <p className="text-sm text-green-600 font-semibold mb-3 text-center">✓ Signature Saved</p>
            <img 
              src={savedSignature} 
              alt="Saved Signature" 
              className="w-full h-64 object-contain border-2 border-gray-300 rounded-lg"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleRemove}
                className="flex-1 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                🗑️ Remove & Redo
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Use This Signature
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl overflow-hidden">
            {/* Canvas Container */}
            <div className="border-4 border-amber-400 bg-white">
              <SignatureCanvas
                ref={sigRef}
                penColor="black"
                canvasProps={{
                  className: 'w-full touch-none',
                  style: { 
                    height: '320px',
                    touchAction: 'none',
                    maxHeight: '70vh'
                  }
                }}
              />
            </div>
            
            {/* Action Buttons */}
            <div className="p-4 bg-gray-50 flex gap-3">
              <button
                onClick={handleClear}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-5 h-5" />
                Clear
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Save Signature
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer Help Text */}
      <div className="bg-gray-800 p-3">
        <p className="text-xs text-gray-400 text-center">
          💡 Tip: Rotate your device horizontally for more signing space
        </p>
      </div>
    </div>
  );
};

export default SignatureModal;
